import { useState } from "react";
import { apiPost } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingBlock } from "@/components/common/StateViews";
import { SeverityChip } from "@/components/common/SeverityChip";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { fmtCurrency } from "@/lib/format";
import { toast } from "sonner";
import { Wand2, Copy, CheckCircle2 } from "lucide-react";

export default function PromptStudio() {
  const [prompt, setPrompt] = useState("");
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!prompt.trim()) return;
    setBusy(true); setOut(null);
    try { setOut(await apiPost("/ai/promptforge", { prompt })); }
    catch { toast.error("PromptForge unavailable — check the active LLM engine."); } finally { setBusy(false); }
  };
  const copy = () => { navigator.clipboard.writeText(out.improved_prompt); toast.success("Improved prompt copied"); };

  return (
    <div>
      <PageHeader eyebrow="Navitra Companion · PromptForge" title="Prompt Studio" subtitle="Paste a prompt and get an improved version, a missing-context checklist, a caching tip and cost by model." />
      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="font-display text-lg">Your prompt</CardTitle></CardHeader>
          <CardContent>
            <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Paste your prompt here…" className="min-h-[220px]" data-testid="forge-input" />
            <Button className="mt-3 w-full" onClick={run} disabled={busy} data-testid="forge-run"><Wand2 className="h-4 w-4 mr-1.5" />{busy ? "Improving…" : "Improve prompt"}</Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="font-display text-lg">Improved</CardTitle></CardHeader>
          <CardContent>
            {busy ? <LoadingBlock className="h-56" /> : !out ? <p className="text-sm text-muted-foreground py-16 text-center">Your improved prompt will appear here.</p> : (
              <div className="space-y-4" data-testid="forge-output">
                <div className="rounded-lg border bg-muted/30 p-3"><p className="text-sm whitespace-pre-line">{out.improved_prompt}</p><Button size="sm" variant="outline" className="mt-2" onClick={copy}><Copy className="h-3.5 w-3.5 mr-1.5" />Copy</Button></div>
                {out.recommended_model_tier && <div><SeverityChip level="info" label={out.recommended_model_tier} /></div>}
                {out.missing_context?.length > 0 && <div><p className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">Missing context</p><ul className="space-y-1">{out.missing_context.map((m, i) => <li key={i} className="text-sm flex gap-2"><CheckCircle2 className="h-4 w-4 text-gold shrink-0 mt-0.5" />{m}</li>)}</ul></div>}
                {out.caching_tip && <p className="text-sm border-l-2 border-gold/40 pl-3">{out.caching_tip}</p>}
                {out.est_cost_by_model?.length > 0 && <div><p className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">Estimated cost</p>{out.est_cost_by_model.map((c, i) => <div key={i} className="flex justify-between text-sm border-b border-dashed py-1"><span className="mono text-xs">{c.model}</span><span className="mono">{fmtCurrency(c.usd)}</span></div>)}</div>}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
